export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const UNAUTHORIZED_EVENT = 'auth:unauthorized'

export type QueryParams = Record<
  string,
  string | number | boolean | string[] | undefined | null
>

/** Arrays are joined with commas to match the server's comma-separated query params. */
export function toQueryString(params?: QueryParams): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(','))
      continue
    }
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'REQUEST_FAILED'
  let message = 'הפעולה נכשלה'
  let details: unknown

  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string; details?: unknown } }
    if (body.error) {
      code = body.error.code ?? code
      message = body.error.message ?? message
      details = body.error.details
    }
  } catch {
    // Not a JSON error body — keep the defaults.
  }

  return new ApiError(res.status, code, message, details)
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, { credentials: 'include', ...init })

  if (res.status === 401) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    throw new ApiError(401, 'UNAUTHORIZED', 'ההתחברות פגה — יש להתחבר מחדש')
  }
  if (!res.ok) throw await toApiError(res)
  if (res.status === 204) return undefined as T

  return (await res.json()) as T
}

const jsonInit = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: body === undefined ? undefined : JSON.stringify(body),
})

export const api = {
  get: <T>(path: string, params?: QueryParams) => request<T>(`${path}${toQueryString(params)}`),
  post: <T>(path: string, body?: unknown) => request<T>(path, jsonInit('POST', body)),
  patch: <T>(path: string, body?: unknown) => request<T>(path, jsonInit('PATCH', body)),
  put: <T>(path: string, body?: unknown) => request<T>(path, jsonInit('PUT', body)),
  del: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', body: formData }),
}

/** Streams a file to the browser without leaving the SPA. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`/api${path}`, { credentials: 'include' })
  if (res.status === 401) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    throw new ApiError(401, 'UNAUTHORIZED', 'ההתחברות פגה — יש להתחבר מחדש')
  }
  if (!res.ok) throw await toApiError(res)

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fallbackName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
